import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import CustomInput from "../../common/CustomInput";
import RoleAPIs from "../../../apiRoutes/Role";
import { GET_PERMISSIONS_QUERY_KEY, GET_ROLES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import toast from "react-hot-toast";
import { normalizeRoleName, validateRoleName, ROLE_NAME_MAX_LENGTH } from "../../../utils/constants/Roles";

const AddRoleModal = ({ open, onClose, editData = null }) => {
    const [formData, setFormData] = useState({
        name: editData?.name || "",
        permission_ids: editData?.permission_ids ?? editData?.permissions?.map(p => p.id) ?? [],
    });
    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();

    const { data: permissionsResult } = useQuery({
        queryKey: GET_PERMISSIONS_QUERY_KEY,
        queryFn: () => RoleAPIs.listPermissions({ axiosPrivate, limit: 100, offset: 0 }),
    });
    const permissions = permissionsResult?.data ?? [];

    const { mutate: submitRole, isPending } = useMutation({
        mutationFn: (payload) =>
            editData
                ? RoleAPIs.update({ axiosPrivate, id: editData.id, payload })
                : RoleAPIs.create({ axiosPrivate, payload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GET_ROLES_QUERY_KEY });
            toast.success(`Role ${editData ? 'updated' : 'created'} successfully!`);
            onClose();
        },
        onError: (error) => {
            toast.error(error?.response?.data?.error_message || error?.message);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        const name = editData?.is_protected ? formData.name : normalizeRoleName(formData.name);
        const err = validateRoleName(name);
        if (err) return toast.error(err);
        submitRole({ ...formData, name });
    };

    return (
        <CustomModal
            title={editData ? "Edit Role" : "Add New Role"}
            open={open}
            onClose={onClose}
            handleConfirmation={handleSubmit}
            isLoading={isPending}
            handleConfirmationBtnText={editData ? "Update" : "Create"}
        >
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto px-1">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                    <CustomInput
                        placeholder="e.g. ad_manager"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: editData?.is_protected ? e.target.value : normalizeRoleName(e.target.value) })}
                        disabled={editData?.is_protected}
                        maxLength={ROLE_NAME_MAX_LENGTH}
                    />
                    <p className="text-[10px] text-gray-500 mt-1">{formData.name.length}/{ROLE_NAME_MAX_LENGTH} • lowercase, a–z, 0–9, . _ only</p>
                    {editData?.is_protected && (
                        <p className="text-[10px] text-blue-600 mt-1 font-medium">Protected roles cannot have their names changed.</p>
                    )}
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Permissions</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-gray-100">
                        {permissions?.map(perm => (
                            <label key={perm.id} className="inline-flex items-center p-2 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:border-primaryPurple/30 transition-colors">
                                <input
                                    type="checkbox"
                                    className="rounded text-primaryPurple focus:ring-primaryPurple mr-2"
                                    checked={formData.permission_ids.includes(perm.id)}
                                    onChange={(e) => {
                                        const ids = e.target.checked
                                            ? [...formData.permission_ids, perm.id]
                                            : formData.permission_ids.filter(id => id !== perm.id);
                                        setFormData({ ...formData, permission_ids: ids });
                                    }}
                                />
                                <div className="flex flex-col">
                                    <span className="text-[11px] font-bold text-gray-800">{perm.code}</span>
                                    {perm.description && <span className="text-[9px] text-gray-500 line-clamp-1">{perm.description}</span>}
                                </div>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </CustomModal>
    );
};

export default AddRoleModal;
